// @ts-nocheck

import React from 'react';
import type { ComponentType } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router-dom';

import AppLayout from '../AppLayout';
import { NotFound } from '../components';
import { Login } from '../containers/Login';
import Logout from '../components/Logout';
import { accessTypeEnum } from '../lib/enum';
import { RouteComponent, RouterAdapter } from '../lib/routerAdapter';
import routes from './index';

export type RouteMetadata = {
  component: ComponentType<any>;
  path: string;
  access: string[];
};

type DefaultRouteProps = {
  userAccess: string[];
};

const routeMetadata: RouteMetadata[] = routes as RouteMetadata[];

const hasAccess = (userAccess: string[], routeAccess: string[]): boolean =>
  userAccess.some((access) => routeAccess.includes(access));

export const getAccessibleRoutes = (
  userAccess: string[],
  routeList: RouteMetadata[] = routeMetadata
): RouteMetadata[] =>
  routeList.filter((route) => hasAccess(userAccess, route.access));

type DefaultRouteRule = {
  path: string;
  access: string[];
};

const defaultRouteRules: DefaultRouteRule[] = [
  {
    path: '/dashboard',
    access: [
      accessTypeEnum.admin,
      accessTypeEnum.adminBranch,
      accessTypeEnum.viewingDashboard,
    ],
  },
  {
    path: '/vehicles',
    access: [
      accessTypeEnum.creatingVehicle,
      accessTypeEnum.handlingOrder,
      accessTypeEnum.viewingVehicleBranch,
      accessTypeEnum.viewingVehicleServiceOnly,
      accessTypeEnum.viewingAllVehicles,
      accessTypeEnum.approvingVehicle,
    ],
  },
  {
    path: '/admin/drivers',
    access: [accessTypeEnum.handlingDriver, accessTypeEnum.viewingAllDrivers],
  },
  {
    path: '/driverRedirect/',
    access: [accessTypeEnum.viewingPddTests],
  },
];

export const getDefaultRoute = (userAccess: string[]): string =>
  defaultRouteRules.find(({ access }) => hasAccess(userAccess, access))?.path ??
  '/driverRedirect/';

const DefaultRoute = ({ userAccess }: DefaultRouteProps) => (
  <Navigate replace to={getDefaultRoute(userAccess)} />
);

export default function AppRoutes() {
  const userAccess = useSelector(
    (state: any): string[] => state.auth.profile?.access || []
  );
  const accessibleRoutes = getAccessibleRoutes(userAccess);

  return (
    <>
      <RouterAdapter />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/logout" element={<Logout />} />
        <Route
          path="/"
          element={
            <AppLayout>
              <DefaultRoute userAccess={userAccess} />
            </AppLayout>
          }
        />
        {accessibleRoutes.map(({ component: Component, path }) => (
          <Route
            key={path}
            path={path}
            element={
              <AppLayout>
                <RouteComponent component={Component} />
              </AppLayout>
            }
          />
        ))}
        <Route
          path="*"
          element={
            <AppLayout>
              <NotFound />
            </AppLayout>
          }
        />
      </Routes>
    </>
  );
}
